import {
  computed,
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Renderer2,
  ElementRef,
  inject,
  LOCALE_ID,
  viewChild,
  signal,
  model,
} from '@angular/core';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { CdkConnectedOverlay, ConnectedPosition, Overlay } from '@angular/cdk/overlay';
import { NavigationService, VirtualKeyboardButton, VirtualKeyboardLayout } from '@cue/navigation-services';
import {
  cueClose,
  cueDeleteBackspace,
  cueSpacebar,
  cueEnter,
  cueSpecialChars,
  cueMove,
  IconComponent,
} from '@cue/ui/icons';
import { translation } from '@cue/translations';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { NAV_STORAGE_KEYS } from '../../storage-keys';
import { VIRTUAL_KEYBOARD_MODE_ATTRIBUTE, VirtualKeyboardMode } from '../../directives/virtual-keyboard-mode.directive';
import { createNumericKeyboardLayout, getVirtualKeyboardDecimalSeparator } from './numeric-keyboard-layout';

type VirtualKeyboardTarget = HTMLInputElement | HTMLTextAreaElement;

@Component({
  selector: 'cue-virtual-keyboard',
  imports: [IconComponent, TranslocoPipe, NgTemplateOutlet, NgClass, CdkConnectedOverlay, CdkDrag, CdkDragHandle],
  templateUrl: './virtual-keyboard.component.html',
  styleUrl: './virtual-keyboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VirtualKeyboardComponent {
  private navigationService = inject(NavigationService);
  private renderer = inject(Renderer2);
  private locale = inject(LOCALE_ID);

  show = model(false);
  keyboardRef = viewChild<ElementRef<HTMLElement>>('keyboardRef');
  private keyboardDrag = viewChild(CdkDrag);
  protected keyboardOrigin = signal<ElementRef<HTMLElement> | VirtualKeyboardTarget>(inject(ElementRef));
  protected scrollStrategy = inject(Overlay).scrollStrategies.reposition();
  protected keyboardPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 8, offsetY: -15 },
    { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: -8, offsetY: -15 },
    { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
    { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
  ];

  private lastActiveElement: VirtualKeyboardTarget | null = null;
  private readonly defaultKeyboardLayout = this.navigationService.getKeyboardLayout(null);
  private readonly numericKeyboardLayout = createNumericKeyboardLayout(getVirtualKeyboardDecimalSeparator(this.locale));

  protected readonly keyboardMode = signal<VirtualKeyboardMode>('default');
  protected readonly keyboardLayout = signal<VirtualKeyboardLayout>(this.defaultKeyboardLayout);
  protected readonly keyboardRows = computed(() => {
    const layout = this.keyboardLayout();

    return [layout.firstRow, layout.secondRow, layout.thirdRow, layout.fourthRow, layout.fifthRow].filter(
      (row) => row.length > 0
    );
  });
  protected isShiftActive = false;
  protected isCtrlActive = false;
  protected isCapsLockActive = false;
  protected translation = translation;

  ICONS = {
    cueClose,
    cueDeleteBackspace,
    cueSpacebar,
    cueEnter,
    cueSpecialChars,
    cueMove,
  };

  saveVirtualKeyboard(wantToShowKeyboard: boolean) {
    this.show.set(wantToShowKeyboard);
    localStorage.setItem(NAV_STORAGE_KEYS.showVirtualKeyboard, wantToShowKeyboard + '');

    if (!wantToShowKeyboard) {
      this.setKeyboardMode('default');
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target;
    const keyboardElement = this.keyboardRef()?.nativeElement;

    const isInsideKeyboard = target instanceof Node && keyboardElement?.contains(target);

    if (this.showForTarget(target)) {
      return;
    }

    if (!isInsideKeyboard) {
      this.saveVirtualKeyboard(false);
    }
  }

  @HostListener('document:focusin', ['$event'])
  onDocumentFocusIn(event: FocusEvent): void {
    this.showForTarget(event.target);
  }

  private showForTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLTextAreaElement)) {
      return false;
    }

    this.lastActiveElement = target;
    this.setKeyboardMode(this.resolveKeyboardMode(target));
    this.setPosition(target);
    this.saveVirtualKeyboard(true);
    return true;
  }

  protected setPosition(element: VirtualKeyboardTarget): void {
    this.keyboardDrag()?.reset();
    this.keyboardOrigin.set(element);
  }

  protected isSpecialKeys(key: VirtualKeyboardButton): boolean {
    return key.isEnterFn || key.isCtrlFn || key.isCapsLockFn || key.isShiftFn || key.isDeleteFn || key.isCloseFn;
  }

  protected handleKeyPress(key: VirtualKeyboardButton, event: Event): void {
    event.preventDefault();
    const activeElement = this.lastActiveElement;
    if (!activeElement) return;

    const { start, end } = this.getSelection(activeElement);
    const value = activeElement.value;

    if (this.isSpecialKeys(key)) {
      this.handleSpecialKeyPressed(key);
    } else {
      let character = '';

      if (key.isSpaceFn) {
        character = ' ';
      } else if (this.isCtrlActive) {
        character = key.ctrlValue;
      } else {
        character =
          this.isShiftActive || this.isCapsLockActive
            ? key.shiftValue || key.defaultValue.toUpperCase()
            : key.defaultValue.toLowerCase();
      }

      const newValue = value.slice(0, start) + character + value.slice(end);
      this.renderer.setProperty(activeElement, 'value', newValue);
      this.setSelectionRange(activeElement, start + character.length);

      if (this.isShiftActive) {
        this.isShiftActive = false;
      }
    }

    this.triggerInputEvent(activeElement);
  }

  private handleSpecialKeyPressed(key: VirtualKeyboardButton) {
    if (key.isCapsLockFn) {
      this.isCapsLockActive = !this.isCapsLockActive;
    } else if (key.isShiftFn) {
      this.isShiftActive = !this.isShiftActive;
    } else if (key.isDeleteFn) {
      if (this.lastActiveElement) {
        const { start, end } = this.getSelection(this.lastActiveElement);
        const value = this.lastActiveElement.value;

        if (start !== end) {
          const newValue = value.slice(0, start) + value.slice(end);
          this.renderer.setProperty(this.lastActiveElement, 'value', newValue);
          this.setSelectionRange(this.lastActiveElement, start);
        } else if (start > 0) {
          const newValue = value.slice(0, start - 1) + value.slice(end);
          this.renderer.setProperty(this.lastActiveElement, 'value', newValue);
          this.setSelectionRange(this.lastActiveElement, start - 1);
        }
      }
    } else if (key.isCtrlFn) {
      this.isCtrlActive = !this.isCtrlActive;
    } else if (key.isEnterFn) {
      this.lastActiveElement?.blur();
    } else if (key.isCloseFn) {
      this.lastActiveElement?.blur();
      this.saveVirtualKeyboard(false);
    }
  }

  protected getKeyAriaLabel(key: VirtualKeyboardButton): string {
    if (key.isDeleteFn) return 'Backspace';
    if (key.isEnterFn) return 'Enter';
    if (key.isCloseFn) return 'Close keyboard';
    if (key.isSpaceFn) return 'Space';
    if (key.isCtrlFn) return 'Special characters';
    if (key.isShiftFn) return 'Shift';
    if (key.isCapsLockFn) return 'Caps Lock';

    return key.defaultValue;
  }

  private resolveKeyboardMode(element: VirtualKeyboardTarget): VirtualKeyboardMode {
    const explicitMode = element.getAttribute(VIRTUAL_KEYBOARD_MODE_ATTRIBUTE);
    if (explicitMode === 'default' || explicitMode === 'numeric') {
      return explicitMode;
    }

    const inputMode = (element.inputMode || '').toLowerCase();
    if (
      (element instanceof HTMLInputElement && element.type.toLowerCase() === 'number') ||
      inputMode === 'numeric' ||
      inputMode === 'decimal' ||
      element.getAttribute('role')?.toLowerCase() === 'spinbutton'
    ) {
      return 'numeric';
    }

    return 'default';
  }

  private setKeyboardMode(mode: VirtualKeyboardMode): void {
    this.keyboardMode.set(mode);
    this.keyboardLayout.set(mode === 'numeric' ? this.numericKeyboardLayout : this.defaultKeyboardLayout);
  }

  private getSelection(element: VirtualKeyboardTarget): { start: number; end: number } {
    const fallbackPosition = element.value.length;

    return {
      start: element.selectionStart ?? fallbackPosition,
      end: element.selectionEnd ?? fallbackPosition,
    };
  }

  private setSelectionRange(element: VirtualKeyboardTarget, position: number): void {
    if (element.selectionStart === null || element.selectionEnd === null) {
      return;
    }

    element.setSelectionRange(position, position);
  }

  private triggerInputEvent(element: VirtualKeyboardTarget): void {
    const event = new Event('input', { bubbles: true });
    element.dispatchEvent(event);
  }
}
